import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, 
  BarChart3, 
  PieChart, 
  TrendingUp, 
  Users, 
  Map,
  Download,
  RefreshCw,
  Share2,
  Calendar,
  MapPin,
  Globe,
  Lock,
  Filter
} from 'lucide-react';
import ShareResults from '../components/ShareResults';
import Chart from 'chart.js/auto';
import { useCountry } from '../contexts/CountryContext';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Nigeria States for filtering
const NIGERIA_STATES = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
  'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe',
  'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara',
  'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau',
  'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

// Date range options
const DATE_RANGES = [
  { id: 'all', label: 'All Time', labelFr: 'Tout le temps' },
  { id: '7d', label: 'Last 7 Days', labelFr: '7 derniers jours' },
  { id: '30d', label: 'Last 30 Days', labelFr: '30 derniers jours' },
  { id: '90d', label: 'Last 90 Days', labelFr: '90 derniers jours' },
  { id: '2025', label: '2025', labelFr: '2025' },
  { id: '2024', label: '2024', labelFr: '2024' }
];

export default function AnalyticsDashboard({ user, isAdmin }) {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { country, countries } = useCountry();
  const { language } = useLanguage();
  
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(surveyId || '');
  const [analytics, setAnalytics] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // New filter states
  const [selectedState, setSelectedState] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [viewLevel, setViewLevel] = useState('country'); // 'country' or 'state'

  // Get current country name
  const currentCountry = countries.find(c => c.code === country) || { name: 'Nigeria', code: 'NG' };
  const reportName = `Citizen Voice ${currentCountry.name}`;

  const sentimentChartRef = useRef(null);
  const stateChartRef = useRef(null);
  const sentimentChartInstance = useRef(null);
  const stateChartInstance = useRef(null);

  useEffect(() => {
    fetchSurveys();
    fetchOverview();
  }, []);

  useEffect(() => {
    if (selectedSurvey) {
      fetchAnalytics(selectedSurvey);
    }
  }, [selectedSurvey]);

  useEffect(() => {
    if (analytics) {
      renderCharts();
    }
    return () => {
      if (sentimentChartInstance.current) sentimentChartInstance.current.destroy();
      if (stateChartInstance.current) stateChartInstance.current.destroy();
    };
  }, [analytics]);

  const fetchSurveys = async () => {
    try {
      const response = await fetch(`${API}/surveys`);
      if (response.ok) {
        const data = await response.json();
        setSurveys(data);
        if (!selectedSurvey && data.length > 0) {
          setSelectedSurvey(data[0].survey_id);
        }
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  const fetchOverview = async () => {
    try {
      const response = await fetch(`${API}/analytics/overview`);
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
      }
    } catch (error) {
      console.error('Error fetching overview:', error);
    }
  };

  const fetchAnalytics = async (surveyId) => {
    setLoading(true);
    try {
      // Build query params with filters
      const params = new URLSearchParams();
      if (selectedState !== 'all') params.append('state', selectedState);
      if (dateRange !== 'all') params.append('date_range', dateRange);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API}/analytics/survey/${surveyId}${queryString}`);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when filters change
  useEffect(() => {
    if (selectedSurvey) {
      fetchAnalytics(selectedSurvey);
    }
  }, [selectedState, dateRange]);

  const renderCharts = () => {
    // Sentiment Pie Chart
    if (sentimentChartRef.current && analytics?.sentiment_distribution) {
      if (sentimentChartInstance.current) sentimentChartInstance.current.destroy();
      
      const ctx = sentimentChartRef.current.getContext('2d');
      sentimentChartInstance.current = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Positive', 'Neutral', 'Negative'],
          datasets: [{
            data: [
              analytics.sentiment_distribution.positive || 0,
              analytics.sentiment_distribution.neutral || 0,
              analytics.sentiment_distribution.negative || 0
            ],
            backgroundColor: ['#059669', '#6B7280', '#DC2626'],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                padding: 20,
                usePointStyle: true
              }
            }
          },
          cutout: '60%'
        }
      });
    }

    // State Bar Chart
    if (stateChartRef.current && analytics?.state_breakdown) {
      if (stateChartInstance.current) stateChartInstance.current.destroy();
      
      const stateData = Object.entries(analytics.state_breakdown)
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, 10);

      const ctx = stateChartRef.current.getContext('2d');
      stateChartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: stateData.map(([state]) => state),
          datasets: [{
            label: 'Responses',
            data: stateData.map(([, data]) => data.count),
            backgroundColor: '#006538',
            borderRadius: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: {
                display: false
              }
            },
            y: {
              grid: {
                display: false
              }
            }
          }
        }
      });
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600 bg-green-100';
      case 'negative': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-dashboard">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to={isAdmin ? "/admin" : "/"}>
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {language === 'fr' ? 'Retour' : 'Back'}
                </Button>
              </Link>
              <div className="h-6 w-px bg-border"></div>
              <div>
                <h1 className="font-semibold">{reportName}</h1>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Tableau de bord analytique' : 'Analytics Dashboard'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Select value={selectedSurvey} onValueChange={setSelectedSurvey}>
                <SelectTrigger className="w-64" data-testid="survey-select">
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner une enquête' : 'Select a survey'} />
                </SelectTrigger>
                <SelectContent>
                  {surveys.map((survey) => (
                    <SelectItem key={survey.survey_id} value={survey.survey_id}>
                      {survey.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Link to="/map">
                <Button variant="outline" size="sm">
                  <Map className="mr-2 h-4 w-4" />
                  {language === 'fr' ? 'Carte' : 'Map View'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Filter Bar */}
      <div className="bg-muted/30 border-b py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" />
              <span>{language === 'fr' ? 'Filtres:' : 'Filters:'}</span>
            </div>
            
            {/* View Level Toggle */}
            <div className="flex items-center bg-white rounded-lg border p-1">
              <Button 
                variant={viewLevel === 'country' ? 'default' : 'ghost'} 
                size="sm"
                className="h-7 text-xs"
                onClick={() => { setViewLevel('country'); setSelectedState('all'); }}
              >
                <Globe className="h-3 w-3 mr-1" />
                {language === 'fr' ? 'National' : 'Country'}
              </Button>
              <Button 
                variant={viewLevel === 'state' ? 'default' : 'ghost'} 
                size="sm"
                className="h-7 text-xs"
                onClick={() => setViewLevel('state')}
              >
                <MapPin className="h-3 w-3 mr-1" />
                {language === 'fr' ? 'État' : 'State'}
              </Button>
            </div>

            {/* State Selector (visible when state view is selected) */}
            {viewLevel === 'state' && (
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger className="w-40 h-8 text-xs">
                  <SelectValue placeholder={language === 'fr' ? 'Tous les états' : 'All States'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous les états' : 'All States'}</SelectItem>
                  {NIGERIA_STATES.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Date Range */}
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-36 h-8 text-xs">
                <Calendar className="h-3 w-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map((range) => (
                  <SelectItem key={range.id} value={range.id}>
                    {language === 'fr' ? range.labelFr : range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Active filters indicator */}
            {(selectedState !== 'all' || dateRange !== 'all') && (
              <Badge variant="secondary" className="text-xs">
                {[
                  selectedState !== 'all' && selectedState,
                  dateRange !== 'all' && (DATE_RANGES.find(r => r.id === dateRange)?.label || dateRange)
                ].filter(Boolean).join(' • ')}
              </Badge>
            )}

            {/* Clear filters */}
            {(selectedState !== 'all' || dateRange !== 'all') && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-xs"
                onClick={() => { setSelectedState('all'); setDateRange('all'); setViewLevel('country'); }}
              >
                {language === 'fr' ? 'Effacer' : 'Clear'}
              </Button>
            )}

            {/* Paid features indicator */}
            <div className="ml-auto flex items-center gap-2">
              <Link to="/reports/purchase">
                <Button variant="outline" size="sm" className="h-7 text-xs">
                  <Lock className="h-3 w-3 mr-1" />
                  {language === 'fr' ? 'Analyses avancées' : 'Advanced Analysis'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="metric-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Responses</p>
                  <p className="text-3xl font-bold">{analytics?.total_responses || 0}</p>
                </div>
                <Users className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">States Covered</p>
                  <p className="text-3xl font-bold">
                    {analytics?.state_breakdown ? Object.keys(analytics.state_breakdown).length : 0}
                  </p>
                </div>
                <Map className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Questions</p>
                  <p className="text-3xl font-bold">{analytics?.survey?.questions?.length || 0}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg. Sentiment</p>
                  <p className="text-3xl font-bold capitalize">
                    {analytics?.sentiment_distribution 
                      ? Object.entries(analytics.sentiment_distribution)
                          .sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'
                      : 'N/A'}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : analytics ? (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Question Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle>Question Breakdown</CardTitle>
                  <CardDescription>Detailed responses for each question</CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="0">
                    <TabsList className="mb-4 flex-wrap h-auto">
                      {analytics.survey?.questions?.map((q, i) => (
                        <TabsTrigger key={q.question_id} value={String(i)} className="text-xs">
                          Q{i + 1}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    
                    {analytics.survey?.questions?.map((question, i) => {
                      const qAnalytics = analytics.question_analytics[question.question_id];
                      return (
                        <TabsContent key={question.question_id} value={String(i)}>
                          <div className="space-y-4">
                            <div>
                              <h4 className="font-medium mb-1">{question.text}</h4>
                              <Badge variant="outline" className="text-xs">
                                {question.question_type.replace('_', ' ')}
                              </Badge>
                            </div>

                            {question.question_type === 'multiple_choice' && qAnalytics?.options && (
                              <div className="space-y-3">
                                {qAnalytics.options.map((opt) => (
                                  <div key={opt.option_id}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                      <span>{opt.text}</span>
                                      <span className="font-mono">{opt.count} ({opt.percentage}%)</span>
                                    </div>
                                    <Progress value={opt.percentage} className="h-2" />
                                  </div>
                                ))}
                              </div>
                            )}

                            {question.question_type === 'rating' && qAnalytics?.distribution && (
                              <div className="space-y-3">
                                <div className="text-center py-4">
                                  <span className="text-4xl font-bold text-primary">
                                    {qAnalytics.average}
                                  </span>
                                  <span className="text-lg text-muted-foreground">/5</span>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Average rating from {qAnalytics.total} responses
                                  </p>
                                </div>
                                <div className="flex items-end justify-center gap-2 h-32">
                                  {[1, 2, 3, 4, 5].map((rating) => {
                                    const count = qAnalytics.distribution[rating] || 0;
                                    const maxCount = Math.max(...Object.values(qAnalytics.distribution));
                                    const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                                    return (
                                      <div key={rating} className="flex flex-col items-center">
                                        <div 
                                          className="w-12 bg-primary rounded-t transition-all"
                                          style={{ height: `${height}%`, minHeight: count > 0 ? '8px' : '0' }}
                                        ></div>
                                        <span className="text-sm font-medium mt-2">{rating}</span>
                                        <span className="text-xs text-muted-foreground">{count}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {question.question_type === 'open_ended' && qAnalytics?.responses && (
                              <div className="space-y-3 max-h-64 overflow-y-auto thin-scrollbar">
                                {qAnalytics.responses.slice(0, 10).map((resp, idx) => (
                                  <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                                    <p className="text-sm">{resp.text}</p>
                                    <div className="flex items-center gap-2 mt-2">
                                      <Badge variant="outline" className="text-xs">{resp.state}</Badge>
                                      <Badge className={`text-xs ${getSentimentColor(resp.sentiment)}`}>
                                        {resp.sentiment}
                                      </Badge>
                                    </div>
                                  </div>
                                ))}
                                {qAnalytics.total > 10 && (
                                  <p className="text-sm text-muted-foreground text-center">
                                    +{qAnalytics.total - 10} more responses
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </TabsContent>
                      );
                    })}
                  </Tabs>
                </CardContent>
              </Card>

              {/* State Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Responses by State</CardTitle>
                  <CardDescription>Top 10 responding states</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <canvas ref={stateChartRef}></canvas>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Sentiment Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Sentiment Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <canvas ref={sentimentChartRef}></canvas>
                  </div>
                </CardContent>
              </Card>

              {/* Survey Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Survey Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Title</p>
                    <p className="font-medium">{analytics.survey?.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <Badge variant="secondary">{analytics.survey?.policy_category}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Level</p>
                    <Badge variant="outline">{analytics.survey?.governance_level}</Badge>
                  </div>
                  {isAdmin && (
                    <Link to={`/admin/reports/${selectedSurvey}`}>
                      <Button className="w-full mt-4">
                        <Download className="mr-2 h-4 w-4" />
                        Generate Report
                      </Button>
                    </Link>
                  )}
                  <div className="mt-3">
                    <ShareResults 
                      surveyId={selectedSurvey}
                      surveyTitle={analytics?.survey?.title}
                      totalResponses={analytics?.total_responses}
                      dominantSentiment={
                        analytics?.sentiment_distribution 
                          ? Object.entries(analytics.sentiment_distribution).sort((a, b) => b[1] - a[1])[0]?.[0] 
                          : 'neutral'
                      }
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Data Available</h3>
              <p className="text-muted-foreground">
                Select a survey to view analytics, or wait for responses to come in.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
